/**
 * useInventoryData — Data-fetching hook for the Inventory page.
 *
 * Loads NetSuite inventory items for a given portco.
 * Follows the same pattern as usePortcoData.ts.
 */

import { useEffect, useState } from "react";
import {
  getPortcoNetSuiteData,
  NetSuiteRow,
  NetSuiteDataResponse,
} from "../utils/PowerBIApiCall";

export interface InventoryData {
  items: NetSuiteRow[];
  portcoName: string | null;
  queries: string[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch all NetSuite data for a portco.
 *
 * @param portcoId  — slug from URL params
 * @param enabled   — gate fetching (e.g. wait for MSAL interaction). Defaults to true.
 * @param onAuthError — optional callback for InteractionRequiredAuthError in live mode
 */
export function useInventoryData(
  portcoId: string | undefined,
  enabled: boolean = true,
  onAuthError?: (error: unknown) => void,
): InventoryData {
  const [items, setItems] = useState<NetSuiteRow[]>([]);
  const [portcoName, setPortcoName] = useState<string | null>(null);
  const [queries, setQueries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!portcoId || !enabled) return;

    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        const data: NetSuiteDataResponse = await getPortcoNetSuiteData(
          portcoId!,
        );

        if (cancelled) return;

        setPortcoName(data.portcoName);
        setQueries(data.queries);

        // Extract inventory from results — inventoryItems (Wellmore) or inventoryBalance (ARG)
        const inventoryRows =
          data.results?.inventoryItems || data.results?.inventoryBalance || [];
        setItems(inventoryRows);
      } catch (e) {
        if (cancelled) return;

        if (onAuthError) {
          const { InteractionRequiredAuthError } =
            await import("@azure/msal-browser");
          if (e instanceof InteractionRequiredAuthError) {
            onAuthError(e);
            return;
          }
        }

        setError(
          e instanceof Error ? e.message : "Failed to load inventory data",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [portcoId, enabled]);

  return { items, portcoName, queries, loading, error };
}
